import { ConfigFeature } from '../../../../features/config/contract'
import { Config, DeviceDataSnapshot, platform } from '../../../../types/config'
import { ADDRESS_TELINK } from '../../protocols/address'
import {
  ensureCybercafeConfigSlotNames,
  getCybercafeConfigSlots,
  getRoleMatchedSlots,
  toFirmwareIndex,
  fromFirmwareIndex,
} from '../../../../utils/configSlots'
import {
  TELINK_KEYBOARD_COMMAND,
  PROTOCOL_CONFIG,
  INIT_DEFAULT_FLASH,
} from '../../protocols/keyboard-command'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { convertASCIIToStr, convertStrToASCII } from '../../../../utils/StringUtils'
import { delay } from '../../../../utils/TimeUtils'
import { sendRead, sendWrite, checkACK } from '../../capabilities/memory'
import { getAddress } from '../../../../utils/common'
import { LayerKeysMap, Macro } from '../../../../types/keyboardKeys'
import {
  KeyboardLightingEffect,
  keyMapLighting,
  staticType,
} from '../../../../types/keyboardLighting'
import { LightingDomainType } from '../../../../types/lightingDomain'
import { LIGHTING_TARGET } from '../../../../types/lighting'
import { keysColor } from '../../../../types/color'
import { isMacOS } from '../../../../utils/envString'
import { formatFirmwareVersions } from '../../../../utils/firmwareVersion'
import { TelinkConfigPayload } from '../../types/TelinkConfigPayload'
import { KeyboardPerfCfg } from '../../../../types/keyboardPerCfg'

const REPORT_ID = PROTOCOL_CONFIG.report_id
const FEATURE_REPORT_ID = PROTOCOL_CONFIG.feature_report_id

export class TelinkConfig extends ConfigFeature {
  async getCurrentConfig(): Promise<Config> {
    const rawIndex = await this.getCurrentConfigIndex()
    const matchedSlots = getRoleMatchedSlots(
      this.device.manifest.capabilities,
      this.device.configSlotRoles,
    )
    const index = fromFirmwareIndex(rawIndex, matchedSlots)
    const name = await this.getCurrentConfigName(index)
    this.device.currentConfig = {
      index,
      name,
    }
    return this.device.currentConfig
  }

  getCurrentConfigIndex(): Promise<number> {
    const ADDRESS = getAddress(ADDRESS_TELINK.CONFIG_CURRENT, 0)

    return sendRead(
      this.device,
      ADDRESS,
      8,
      (data) => {
        return data.getUint8(4)
      },
      [`cfg:${this.device.currentConfig.index}`],
    )
  }

  async getCurrentConfigName(index?: number): Promise<string> {
    const realIndex = index !== undefined ? index : await this.getCurrentConfigIndex()
    const ADDRESS = getAddress(ADDRESS_TELINK.CONFIG_NAME, realIndex)

    return sendRead(
      this.device,
      ADDRESS,
      0x10,
      (data) => {
        const first = data.getUint8(4)
        if (first === 0 || first === 255) {
          return ''
        }
        const array: string[] = []
        for (let i = 4; i < 20; i++) {
          const byte = data.getUint8(i)
          if (byte === 0) break
          array.push(byte.toString(16).padStart(2, '0'))
        }
        return convertASCIIToStr(array)
      },
      [`cfg:${realIndex}`],
    )
  }

  private async getConfigs(filter: boolean = true): Promise<Config[]> {
    const ADDRESS = ADDRESS_TELINK.CONFIG_NAME
    const configs: Config[] = []
    for (const [index, _] of ADDRESS.entries()) {
      const name = await this.getCurrentConfigName(index)
      if (filter && name === '') {
        continue
      }
      configs.push({
        name: name,
        index: index,
      })
    }
    return configs
  }

  async getAllConfigs(): Promise<Config[]> {
    const cafeSlots = getCybercafeConfigSlots(this.device.manifest)
    if (cafeSlots) {
      await ensureCybercafeConfigSlotNames(
        cafeSlots,
        () => this.getConfigs(false),
        (name, index) => this.createConfig(name, index),
      )
    }

    const matchedSlots = getRoleMatchedSlots(
      this.device.manifest.capabilities,
      this.device.configSlotRoles,
    )
    const res = await this.getConfigs(!matchedSlots)
    this.device.configList = res
    return this.device.configList
  }

  async setCurrentConfig(index: number): Promise<void> {
    const matchedSlots = getRoleMatchedSlots(
      this.device.manifest.capabilities,
      this.device.configSlotRoles,
    )
    const firmwareIndex = toFirmwareIndex(index, matchedSlots)
    const ADDRESS = getAddress(ADDRESS_TELINK.CURRENT_CONNECT_PROTOCOL, 0)
    const res = await sendRead(this.device, ADDRESS, 8, (data) => {
      const b0 = data.getUint8(4)
      const b1 = data.getUint8(5)
      const b2 = data.getUint8(6)
      const b3 = data.getUint8(7)
      return [b0, b1, b2, b3]
    })
    res[1] = firmwareIndex
    res[2] = firmwareIndex

    const targetConfig = this.device.configList.find((config) => config.index === index)
    if (targetConfig) {
      this.device.currentConfig = targetConfig
    }
    return sendWrite(this.device, ADDRESS, res)
  }

  private convertAndCheckConfigName(name: string): number[] {
    name = name.trim()
    if (name.length === 0) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '配置名称不能为空')
    }
    const data = convertStrToASCII(name)
    if (data.length > 0x10) {
      throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, '配置名称过长')
    }
    while (data.length < 0x10) {
      data.push(0)
    }
    return data
  }

  renameConfig(config: Config): Promise<void> {
    const ADDRESS = getAddress(ADDRESS_TELINK.CONFIG_NAME, config.index)
    const nameData = this.convertAndCheckConfigName(config.name)
    return sendWrite(this.device, ADDRESS, nameData).then(() => {
      this.device.cache.invalidateByTag(`cfg:${config.index}`)
    })
  }

  async createConfig(name: string, index?: number): Promise<number> {
    name = name.trim()
    if (name.length === 0) {
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, '配置名称不能为空')
    }
    const data = this.convertAndCheckConfigName(name)
    let configIndex = index
    if (configIndex === undefined) {
      const allConfigs = await this.getConfigs(false)
      configIndex = -1
      for (const config of allConfigs) {
        if (config.name === '') {
          configIndex = config.index
          break
        }
      }
      if (configIndex === -1) {
        throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, '无空闲配置')
      }
    }

    const cmd = TELINK_KEYBOARD_COMMAND.add_config
    const connectType = this.device.connectionType
    const payload = new Uint8Array([connectType, cmd[0], configIndex, ...data])

    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })

    checkACK(res)
    await delay(500)
    this.device.cache.invalidateByTag(`cfg:${configIndex}`)
    return configIndex
  }

  async deleteConfig(index: number): Promise<void> {
    const currentConfig = await this.getCurrentConfig()
    if (currentConfig.index === index) {
      await this.setCurrentConfig(0)
    }

    const cmd = TELINK_KEYBOARD_COMMAND.delete_config
    const connectType = this.device.connectionType
    const payload = new Uint8Array([connectType, cmd[0], index])

    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })

    checkACK(res)
    await delay(500)
    this.device.cache.invalidateByTag(`cfg:${index}`)
  }

  private async initDefaultFlash(dafaultType: number): Promise<void> {
    const cmd = TELINK_KEYBOARD_COMMAND.init_default_flash
    const connectType = this.device.connectionType
    const payload = new Uint8Array([connectType, cmd[0], 0x52, dafaultType])

    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })

    checkACK(res)
    this.device.cache.clearAll()
    await delay(2200)
  }

  async initAllConfigs(): Promise<void> {
    await this.initDefaultFlash(INIT_DEFAULT_FLASH.factory)
  }

  /**
   * 获取当前设备的配置数据快照（不含 travel，Telink 无行程功能）
   */
  async getCurrentConfigSnapshot(): Promise<DeviceDataSnapshot> {
    const startTime = Date.now()
    const metadata = {
      deviceType: this.device.manifest.type,
      deviceModel: this.device.manifest.model,
      timestamp: startTime,
      sdkVersion: this.device.sdkVersion,
    }

    const payload = {
      configName: '',
      firmwareVersion: '',
      keyboard: {
        keysMap: [] as LayerKeysMap,
        keysMapFn: [] as LayerKeysMap,
        keysMapMac: [] as LayerKeysMap,
        keysMapMacFn: [] as LayerKeysMap,
        macroList: [] as Macro[],
      },
      lighting: {
        lightingEffect: [] as KeyboardLightingEffect[],
        currentEffect: 0,
        brightness: 60,
        speed: 60,
        customStyle: [] as keysColor[],
        customRGBKeyMap: [] as { index: number; keyMapLighting: keyMapLighting }[],
      },
      sideLighting: {
        enabled: false,
        currentEffect: 0,
        customStyle: [] as keysColor[],
      },
      indicatorLighting: {
        chargeLightFollowAmbient: false,
        ambientLightEnabled: false,
        chargeStyle: [] as keysColor[],
        ambientStyle: [] as keysColor[],
      },
      performanceConfig: {
        cfg: {} as KeyboardPerfCfg,
      },
    }

    const keyboard = this.device.features.keyboardKeys
    const lighting = this.device.features.keyboardLighting
    const firmware = this.device.features.firmware
    const keyboardPerfCfg = this.device.features.keyboardPerfCfg
    if (!keyboard || !lighting || !firmware || !keyboardPerfCfg) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK功能不完整')
    }

    const updateProgress = (progress: number) => {
      this.device.emit('config:Upload_current_config', progress)
    }
    updateProgress(0)

    // ========== 1：基础配置 (5%) ==========
    payload.configName = await this.getCurrentConfigName()
    // 固件版本通过控制命令读取，与 EEPROM 读取可能冲突，捕获异常避免阻塞快照
    try {
      payload.firmwareVersion = formatFirmwareVersions(await firmware.getFirmwareVersion())
    } catch {
      payload.firmwareVersion = ''
    }
    updateProgress(5)

    // ========== 2：灯效基础信息 (10%) -> 累计 15% ==========
    const [lightingEffects, currentEffect] = await Promise.all([
      lighting.getEffects(),
      lighting.getCurrentEffect(),
    ])
    const [brightness, speed] = await Promise.all([
      lighting.getBrightness().catch(() => 0),
      lighting.getSpeed().catch(() => 0),
    ])
    payload.lighting.lightingEffect = lightingEffects
    payload.lighting.currentEffect = currentEffect
    payload.lighting.brightness = brightness
    payload.lighting.speed = speed
    updateProgress(15)

    // ========== 3：灯效详情 (30%) -> 累计 45% ==========
    const lightingDetailPromises = []
    const totalEffects = payload.lighting.lightingEffect.length

    for (const effect of payload.lighting.lightingEffect) {
      const index = effect.id
      const effectMode = effect.kind

      const detailPromise = (async () => {
        let result = null
        if (effectMode === 'dynamic') {
          const { buffer: _buffer, ...rest } = await lighting.getConfig(
            LightingDomainType.Dynamic,
            index,
          )
          result = { type: 'dynamic', data: rest, index }
        } else if (effectMode === 'static') {
          const { buffer: _buffer, ...rest } = await lighting.getConfig(
            LightingDomainType.Static,
            index,
          )
          const keyMapLightingData = [] as keyMapLighting

          if (effect.isCustom) {
            if (lighting.getKeysColorStream) {
              for await (const keyRgb of lighting.getKeysColorStream(
                LightingDomainType.Static,
                index,
              )) {
                keyMapLightingData.push(keyRgb)
              }
            }
          }
          result = { type: 'static', data: rest, keyMapLighting: keyMapLightingData, index }
        }

        return result
      })()
      lightingDetailPromises.push(detailPromise)
    }

    const lightingDetails = []
    for (let i = 0; i < lightingDetailPromises.length; i++) {
      lightingDetails.push(await lightingDetailPromises[i])
      const currentStepProgress = 15 + Math.round((30 * (i + 1)) / totalEffects)
      updateProgress(Math.min(currentStepProgress, 45))
    }

    for (const detail of lightingDetails) {
      if (!detail) continue
      payload.lighting.customStyle.push(detail.data)
      if (detail.type === LightingDomainType.Static) {
        payload.lighting.customRGBKeyMap.push({
          index: detail.index,
          keyMapLighting: detail.keyMapLighting!,
        })
      }
    }
    updateProgress(45)

    // ========== 3.5：指示灯详情 (5%) -> 累计 50% ==========
    try {
      const reg = await lighting.getAuxLightingEnableRegister?.()
      if (reg) {
        payload.indicatorLighting.chargeLightFollowAmbient = reg.chargeLightFollowAmbient
        payload.indicatorLighting.ambientLightEnabled = reg.ambientLightEnabled
      }
    } catch {
      /* no indicator register */
    }
    try {
      const chargeCfg = await lighting.getConfig(LightingDomainType.Indicator, 0)
      payload.indicatorLighting.chargeStyle.push(chargeCfg)
    } catch {
      /* skip charge indicator */
    }
    try {
      const ambientCfg = await lighting.getConfig(LightingDomainType.Indicator, 1)
      payload.indicatorLighting.ambientStyle.push(ambientCfg)
    } catch {
      /* skip ambient indicator */
    }
    updateProgress(50)

    // ========== 4：侧灯详情 (5%) -> 累计 55% ==========
    if (lighting.getSideLightDynamicEnable) {
      payload.sideLighting.enabled = await lighting.getSideLightDynamicEnable()
    }
    if (lighting.getSideEffect) {
      payload.sideLighting.currentEffect = await lighting.getSideEffect()
    }
    try {
      const sideEffects = await lighting.getEffects(LIGHTING_TARGET.Side)
      for (const effect of sideEffects) {
        try {
          const { buffer: _buffer, ...rest } = await lighting.getConfig(
            LightingDomainType.Side,
            effect.id,
          )
          payload.sideLighting.customStyle.push(rest)
        } catch {
          /* skip failed side effect */
        }
      }
    } catch {
      /* no side lighting */
    }
    updateProgress(50)

    // ========== 4：键盘映射 (20%) -> 累计 70% ==========
    const BATCH_SIZE = 22
    const progressStart = 50
    const progressEnd = 65
    const progressRange = progressEnd - progressStart

    const calcTotalChunks = (isFn: boolean, platform?: platform) => {
      const keyCount = keyboard.getLayerKeyCount(isFn, platform)
      return Math.ceil(keyCount / BATCH_SIZE)
    }

    const layers: { isFn: boolean; platform?: platform }[] = isMacOS
      ? [
          { isFn: false, platform: 'mac' },
          { isFn: true, platform: 'mac' },
        ]
      : [{ isFn: false }, { isFn: true }]

    let totalChunks = 0
    for (const layer of layers) {
      totalChunks += calcTotalChunks(layer.isFn, layer.platform)
    }
    let completedChunks = 0

    const readLayerKeysMapStream = async (
      isFnLayer: boolean,
      platform?: 'mac' | 'windows',
    ): Promise<LayerKeysMap> => {
      const result: LayerKeysMap = []
      if (keyboard.getLayerKeysMapStream) {
        const streamArgs: any[] = platform
          ? [isFnLayer, BATCH_SIZE, platform]
          : [isFnLayer, BATCH_SIZE]
        for await (const chunk of keyboard.getLayerKeysMapStream(...streamArgs)) {
          result.push(...chunk)
          completedChunks++
          const progress =
            progressStart + Math.round((progressRange * completedChunks) / totalChunks)
          updateProgress(Math.min(progress, progressEnd))
        }
      }
      return result
    }

    if (isMacOS) {
      const keysMapMac = await readLayerKeysMapStream(false, 'mac')
      payload.keyboard.keysMapMac = keysMapMac
      const keysMapMacFn = await readLayerKeysMapStream(true, 'mac')
      payload.keyboard.keysMapMacFn = keysMapMacFn
    } else {
      const keysMap = await readLayerKeysMapStream(false)
      payload.keyboard.keysMap = keysMap
      const keysMapFn = await readLayerKeysMapStream(true)
      payload.keyboard.keysMapFn = keysMapFn
    }

    // ========== 5：宏数据 (20%) -> 累计 85% ==========
    updateProgress(70)
    if (keyboard.getMacroListStream) {
      for await (const macroChunk of keyboard.getMacroListStream()) {
        payload.keyboard.macroList.push(...macroChunk)
      }
    }

    updateProgress(75)
    const macroList = payload.keyboard.macroList.filter((macro) => macro.index !== undefined)
    const totalMacros = macroList.length

    for (let i = 0; i < macroList.length; i++) {
      const macro = macroList[i]
      const data = await keyboard.getMacroByIndex(macro.index!)
      Object.assign(macro, data)

      const currentStepProgress = 75 + Math.round((10 * (i + 1)) / totalMacros)
      updateProgress(Math.min(currentStepProgress, 85))
    }
    updateProgress(85)

    // ========== 6：性能配置 (10%) -> 累计 95% ==========
    payload.performanceConfig.cfg = await keyboardPerfCfg.getAllPerfCfg()
    updateProgress(95)

    // ========== 7：完成 ==========
    updateProgress(100)

    console.log('TelinkConfigSnapshot: ', payload, metadata)
    return { metadata, payload }
  }

  /**
   * 同步快照数据到当前配置（不含 travel，Telink 无行程功能）
   */
  async setCurrentConfigSnapshot(snapshot: DeviceDataSnapshot) {
    const { metadata, payload } = snapshot as DeviceDataSnapshot<TelinkConfigPayload>

    const keyboard = this.device.features.keyboardKeys
    const lighting = this.device.features.keyboardLighting
    const keyboardPerfCfg = this.device.features.keyboardPerfCfg
    if (!keyboard || !lighting || !keyboardPerfCfg) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK功能不完整')
    }
    if (metadata === undefined || metadata.sdkVersion === undefined) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED_VERSION, '当前使用SDK版本不匹配')
    }
    if (metadata.deviceModel !== this.device.manifest.model) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '设备匹配失败')
    }
    if (metadata.deviceType !== this.device.manifest.type) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '设备匹配失败2')
    }
    if (payload === undefined) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '数据异常')
    }

    const updateProgress = (progress: number) => {
      this.device.emit('config:Download_current_config', progress)
    }
    updateProgress(0)
    updateProgress(5)

    if (payload?.configName) {
      updateProgress(10)
    }

    if (payload?.lighting) {
      const totalEffects = payload.lighting.lightingEffect?.length || 0
      if (totalEffects === 0) {
        updateProgress(40)
      } else {
        updateProgress(10)
        for (let i = 0; i < payload.lighting.lightingEffect.length; i++) {
          const effect = payload.lighting.lightingEffect[i]
          const index = effect.id
          const effectMode = effect.kind
          const style = payload.lighting.customStyle.find(
            (style: keysColor) => style.index === index,
          )
          if (!style) {
            const currentProgress = 10 + Math.round((30 * (i + 1)) / totalEffects)
            updateProgress(Math.min(currentProgress, 40))
            continue
          }
          if (effectMode === 'dynamic') {
            await lighting.setConfig(LightingDomainType.Dynamic, index, {
              brightness: style.brightness ?? 60,
              speed: style.speed ?? 60,
              isColorful: style.isColorful,
              rgb: style.rgb,
            })
          }
          if (effectMode === 'static') {
            if (style.gradientDirection !== undefined) {
              const domain = LightingDomainType.Static
              if (effect.isCustom) {
                await lighting.createCustomEffect(
                  {
                    name: effect.i18nKey,
                    staticType: style.gradientDirection as staticType,
                    brightness: style.brightness ?? 60,
                    rgbGroup: style.rgbGroup,
                  },
                  index,
                )
                const customRGBKeyMap = payload.lighting.customRGBKeyMap.find(
                  (item: { index: number; keyMapLighting: keyMapLighting }) => item.index === index,
                )
                if (customRGBKeyMap) {
                  await lighting.setKeysColor(domain, customRGBKeyMap.keyMapLighting, index)
                }
              } else {
                if (style.gradientDirection === 1) {
                  await lighting.setProperty(domain, index, 'brightness', style.brightness ?? 60)
                }
                if ([2, 3].includes(style.gradientDirection) && style.rgbGroup) {
                  await lighting.setConfig(domain, index, {
                    gradientDirection: style.gradientDirection,
                    brightness: style.brightness ?? 60,
                    rgbGroup: style.rgbGroup,
                  })
                }
              }
            }
          }
          const currentProgress = 10 + Math.round((30 * (i + 1)) / totalEffects)
          updateProgress(Math.min(currentProgress, 40))
        }
      }

      // 新版快照已经在 customStyle 中保存每个灯效各自的亮度/速度。V700DIY 的
      // 全局档位通常仍是出厂默认 60，不能在逐灯效恢复完成后再次写入当前动态灯效，
      // 否则会把刚恢复的 100/100 覆盖成 60/60。
      //
      // 旧版/不完整快照可能没有当前灯效的逐灯效值，此时才沿用顶层字段兜底。
      const currentStyle = payload.lighting.customStyle?.find(
        (style: keysColor) => style.index === payload.lighting.currentEffect,
      )
      if (currentStyle?.brightness === undefined) {
        await lighting.setProperty(
          LightingDomainType.Dynamic,
          payload.lighting.currentEffect,
          'brightness',
          payload.lighting.brightness ?? 60,
        )
      }
      if (currentStyle?.speed === undefined) {
        await lighting.setProperty(
          LightingDomainType.Dynamic,
          payload.lighting.currentEffect,
          'speed',
          payload.lighting.speed ?? 60,
        )
      }
      await lighting.setCurrentEffect(payload.lighting.currentEffect ?? 100)
      updateProgress(45)
    } else {
      updateProgress(45)
    }

    // ========== 3.5：指示灯配置 ==========
    if (payload?.indicatorLighting) {
      if (lighting.setAuxLightingEnableRegister) {
        await lighting.setAuxLightingEnableRegister({
          chargeLightFollowAmbient: payload.indicatorLighting.chargeLightFollowAmbient,
          ambientLightEnabled: payload.indicatorLighting.ambientLightEnabled,
        })
      }
      for (const style of payload.indicatorLighting.chargeStyle) {
        await lighting.setConfig(LightingDomainType.Indicator, 0, {
          brightness: style.brightness,
          rgb: style.rgb,
        })
      }
      for (const style of payload.indicatorLighting.ambientStyle) {
        await lighting.setConfig(LightingDomainType.Indicator, 1, {
          brightness: style.brightness,
          rgb: style.rgb,
        })
      }
    }

    // ========== 4：侧灯配置 ==========
    if (payload?.sideLighting) {
      if (lighting.setSideLightDynamicEnable) {
        await lighting.setSideLightDynamicEnable(payload.sideLighting.enabled)
      }
      for (const style of payload.sideLighting.customStyle) {
        await lighting.setConfig(LightingDomainType.Side, style.index!, {
          brightness: style.brightness,
          speed: style.speed,
          isColorful: style.isColorful,
          rgb: style.rgb,
        })
      }
      if (lighting.setSideEffect && payload.sideLighting.currentEffect !== undefined) {
        await lighting.setSideEffect(payload.sideLighting.currentEffect)
      }
    }

    if (payload?.keyboard) {
      const platform = isMacOS ? 'mac' : 'win'
      const keyMap = isMacOS ? payload.keyboard.keysMapMac : payload.keyboard.keysMap
      const fnKeyMap = isMacOS ? payload.keyboard.keysMapMacFn : payload.keyboard.keysMapFn

      if (keyMap && keyMap.length > 0) {
        const totalKeyMap = keyMap.length
        for (let i = 0; i < totalKeyMap; i++) {
          await keyboard.setLayerKeysMap(keyMap[i], false, platform)
          const currentProgress = 45 + Math.round((10 * (i + 1)) / totalKeyMap)
          updateProgress(Math.min(currentProgress, 55))
        }
      } else {
        updateProgress(55)
      }

      if (fnKeyMap && fnKeyMap.length > 0) {
        const totalFnKeyMap = fnKeyMap.length
        for (let i = 0; i < totalFnKeyMap; i++) {
          await keyboard.setLayerKeysMap(fnKeyMap[i], true, platform)
          const currentProgress = 55 + Math.round((10 * (i + 1)) / totalFnKeyMap)
          updateProgress(Math.min(currentProgress, 65))
        }
      } else {
        updateProgress(65)
      }
    } else {
      updateProgress(65)
    }

    if (payload?.keyboard?.macroList) {
      const macroList = payload.keyboard.macroList.filter((m: Macro) => m.index !== undefined)
      const totalMacros = macroList.length

      if (totalMacros === 0) {
        updateProgress(85)
      } else {
        updateProgress(70)
        for (let i = 0; i < totalMacros; i++) {
          const macro = macroList[i]
          await keyboard.setMacroByIndex(macro, macro.index ?? 0)
          const currentProgress = 70 + Math.round((15 * (i + 1)) / totalMacros)
          updateProgress(Math.min(currentProgress, 85))
        }
      }
    } else {
      updateProgress(85)
    }

    if (payload?.performanceConfig?.cfg) {
      await keyboardPerfCfg.setAllPerfCfg(payload.performanceConfig.cfg)
      updateProgress(95)
    }

    updateProgress(100)
    this.device.cache.invalidateByTag(`cfg:${this.device.currentConfig.index}`)
  }

  /*************** 管理员模式 ********************/
  async initAllConfigsByAdmin(): Promise<void> {
    await this.initDefaultFlash(INIT_DEFAULT_FLASH.admin)
  }

  private async switchAdminMode(mode: number): Promise<void> {
    const cmd = mode
      ? TELINK_KEYBOARD_COMMAND.enter_admin_mode
      : TELINK_KEYBOARD_COMMAND.exit_admin_mode

    const connectType = this.device.connectionType
    const payload = new Uint8Array([connectType, ...cmd])

    const res = await this.device.sendAndWait(REPORT_ID, payload, FEATURE_REPORT_ID, (res) => {
      const ack = res.getUint8(0)
      return ack !== 0x02
    })

    checkACK(res)
    this.device.adminMode = mode === 1
  }

  async enterAdminMode(): Promise<void> {
    this.switchAdminMode(1)
  }

  async exitAdminMode(): Promise<void> {
    this.switchAdminMode(0)
  }
}
